import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/Table';
import { Badge } from '../ui/Badge';
import { LogEntry } from '../../types';
import { ArrowDownIcon, ClockIcon } from 'lucide-react';
interface LogTableProps {
  logs: LogEntry[];
}
export function LogTable({
  logs
}: LogTableProps) {
  const [highlightedRows, setHighlightedRows] = useState<Set<string>>(new Set());
  // Highlight new rows briefly
  useEffect(() => {
    const newIds = logs.filter(l => l.isNew).map(l => l.id);
    if (newIds.length > 0) {
      setHighlightedRows(prev => new Set([...prev, ...newIds]));
      const timer = setTimeout(() => {
        setHighlightedRows(prev => {
          const next = new Set(prev);
          newIds.forEach(id => next.delete(id));
          return next;
        });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [logs]);
  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    const datePart = date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
    const timePart = date.toLocaleTimeString('es-ES', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    return `${datePart} ${timePart}`;
  };
  const getStatusBadge = (status: string, code: number) => {
    const label = code >= 200 && code < 300 ? `SC-${code}` : `SC-${code}`;
    if (code >= 200 && code < 300) return <Badge variant="success" data-id="element-72">{label}</Badge>;
    if (code >= 400 && code < 500) return <Badge variant="warning" data-id="element-73">{label}</Badge>;
    if (code >= 500) return <Badge variant="destructive" data-id="element-74">{label}</Badge>;
    return <Badge variant="secondary" data-id="element-75">{label}</Badge>;
  };
  return <div className="rounded-md border border-gray-200 bg-white overflow-hidden shadow-sm" data-id="element-76">
      <Table data-id="element-77">
        <TableHeader className="bg-gray-50" data-id="element-78">
          <TableRow className="hover:bg-transparent" data-id="element-79">
            <TableHead className="w-[220px]" data-id="element-80">
              <div className="flex items-center space-x-1 cursor-pointer hover:text-foreground" data-id="element-81">
                <ClockIcon className="w-3 h-3 mr-1" data-id="element-82" />
                <span data-id="element-83">Fecha y Hora</span>
                <ArrowDownIcon className="w-3 h-3" data-id="element-84" />
              </div>
            </TableHead>
            <TableHead className="w-[140px]" data-id="element-85">Código de Cliente</TableHead>
            <TableHead className="w-[120px]" data-id="element-86">Estado</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody data-id="element-87">
          {logs.length === 0 ? <TableRow data-id="element-88">
              <TableCell colSpan={3} className="h-24 text-center text-muted-foreground" data-id="element-89">
                No se encontraron logs con los filtros aplicados.
              </TableCell>
            </TableRow> : logs.map(log => <TableRow key={log.id} className={`
                  transition-colors duration-500 font-mono text-xs
                  ${highlightedRows.has(log.id) ? 'bg-primary/10' : ''}
                  ${log.statusCode >= 500 ? 'bg-destructive/5 hover:bg-destructive/10 border-l-2 border-l-destructive' : ''}
                  ${log.statusCode >= 400 && log.statusCode < 500 ? 'border-l-2 border-l-yellow-500' : ''}
                  ${log.statusCode >= 200 && log.statusCode < 300 ? 'border-l-2 border-l-green-500' : ''}
                `} data-id="element-90">
                <TableCell className="text-muted-foreground whitespace-nowrap" data-id="element-91">
                  {formatDateTime(log.timestamp)}
                </TableCell>
                <TableCell data-id="element-92">
                  <span className="px-2 py-1 rounded bg-muted text-muted-foreground font-medium" data-id="element-93">
                    {log.clientCode}
                  </span>
                </TableCell>
                <TableCell data-id="element-94">
                  {getStatusBadge(log.status, log.statusCode)}
                </TableCell>
              </TableRow>)}
        </TableBody>
      </Table>
    </div>;
}